import { encryptPayloadECB } from './crypto'

export type MonkeeConfig = Window['monkeeTranscription']

export type FileItem = {
  fileId: string
  title: string
  type: string | null
  bytes: number
  status: string
  currentState: string | null
  currentSubState: string | null
  progress: number | null
  message: string | null
  createdAt: string | null
  completedAt: string | null
  transcriptionCompletedAt?: number | null
  analysisCompletedAt?: number | null
  // Novos campos para estado de processamento detalhado
  processingState?: 'PENDING' | 'UPLOADING' | 'TRANSCRIBING' | 'PROCESSING_TRANSCRIPTION' | 'ANALYZING' | 'COMPLETED' | 'FAILED' | null
  estimatedSecondsRemaining?: number | null
  processingStartedAt?: number | null
  estimatedCompletionAt?: number | null
}

export type FileListResponse = {
  files: FileItem[]
  totalFiles: number
  page: number
  pageSize: number
  totalPages: number
}

// Novo tipo para a listagem baseada em TranscriptionAnalysis
export type TranscriptionListItem = {
  fileId: string
  title: string
  extension: string | null
  bytes: number | null
  processingState: 'PENDING' | 'UPLOADING' | 'TRANSCRIBING' | 'PROCESSING_TRANSCRIPTION' | 'ANALYZING' | 'COMPLETED' | 'FAILED' | null
  processingSubState: string | null
  progressPercent: number
  progressMessage: string | null
  estimatedSecondsRemaining: number | null
  createdAt: number | null
  processingStartedAt: number | null
  estimatedCompletionAt: number | null
  transcriptionCompletedAt: number | null
  analysisCompletedAt: number | null
  status: string | null
}

export type TranscriptionListResponse = {
  files: TranscriptionListItem[]
  totalFiles: number
  page: number
  pageSize: number
  totalPages: number
}

type UploadCreateResponse = {
  fileId: string
  presignedUrl: string
  headers: Record<string, string>
}

type DownloadResponse = {
  fileId: string
  title: string
  transcriptionUrl: string
  expiresIn: number
}

type StatusResponse = {
  status: string
  currentState: string | null
  currentSubState: string | null
  progress: number | null
  message: string | null
}

type ClientConfig = {
  baseUrl: string
  aiBaseUrl?: string
  apiKey: string
  secret?: string
  userEmail?: string
  userId?: string
  tennantId?: string
}

const envConfig: ClientConfig | null = (() => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined
  const apiKey = import.meta.env.VITE_API_KEY as string | undefined
  if (!baseUrl || !apiKey) return null
  return {
    baseUrl,
    aiBaseUrl: import.meta.env.VITE_AI_API_BASE_URL as string | undefined,
    apiKey,
    secret: import.meta.env.VITE_API_SECRET as string | undefined,
    userEmail: import.meta.env.VITE_API_USER_EMAIL as string | undefined,
    userId: import.meta.env.VITE_API_TEST_USER as string | undefined,
    tennantId: import.meta.env.VITE_API_TEST_TENNANT as string | undefined
  }
})()

// Em desenvolvimento, popula window.monkeeTranscription a partir do .env
if (import.meta.env.DEV && envConfig && !window.monkeeTranscription) {
  console.log('Populando window.monkeeTranscription a partir do .env')
  console.log(envConfig)
  window.monkeeTranscription = {
    apiBaseUrl: envConfig.baseUrl,
    aiBaseUrl: envConfig.aiBaseUrl || 'http://localhost:8080/ai',
    apiKey: envConfig.apiKey,
    encryptedKey: envConfig.secret || '',
    userEmail: envConfig.userEmail || 'dev@local.com',
    userId: envConfig.userId ? parseInt(envConfig.userId, 10) : 0,
    tennantId: envConfig.tennantId,
    ajaxUrl: '',
    restUrl: envConfig.baseUrl,
    nonce: ''
  }
}

function resolveConfig(): ClientConfig | null {
  if (window.monkeeTranscription?.apiBaseUrl && window.monkeeTranscription.apiKey) {
    const { apiBaseUrl, aiBaseUrl, apiKey, encryptedKey, userEmail, userId } = window.monkeeTranscription
    return { 
      baseUrl: apiBaseUrl,
      aiBaseUrl: aiBaseUrl || 'http://localhost:8080/ai',
      apiKey, 
      secret: encryptedKey, 
      userEmail,
      userId: userId?.toString(),
      tennantId: window.monkeeTranscription.tennantId as string | undefined
    }
  }
  return envConfig
}

function buildFileUrl(path: string, cfg: ClientConfig) {
  const base = cfg.baseUrl.replace(/\/$/, '')
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`
}

function buildAiUrl(path: string, cfg: ClientConfig) {
  const base = (cfg.aiBaseUrl || 'http://localhost:8080/ai').replace(/\/$/, '')
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  return `${base}${cleanPath}`
}

async function buildHeaders(cfg: ClientConfig): Promise<HeadersInit> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'x-auth-token': cfg.apiKey
  }

  // Gerar X-WordPress-API-Key criptografado
  if (cfg.userId && cfg.tennantId && cfg.secret) {
    try {
      const encryptedKey = await encryptPayloadECB(
        { userId: cfg.userId, tennantId: cfg.tennantId },
        cfg.secret
      )
      headers['X-WordPress-API-Key'] = encryptedKey
    } catch (e) {
      console.error('Erro ao gerar header criptografado:', e)
      throw new Error('Falha ao gerar autenticação')
    }
  }

  return headers
}

async function request<T>(input: RequestInfo, init: RequestInit = {}): Promise<T> {
  const res = await fetch(input, init)
  const text = await res.text()
  let data: any = null
  try {
    data = text ? JSON.parse(text) : null
  } catch (e) {
    console.error('Erro ao interpretar resposta', e, text)
  }
  if (!res.ok) {
    const msg = data?.message || res.statusText
    throw new Error(msg)
  }
  return data?.data ?? data
}

export async function listFiles(params?: { page?: number; size?: number; sort?: string }): Promise<FileListResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const { page = 0, size = 20, sort = 'createdAt,desc' } = params || {}
  const url = buildFileUrl(`/api/v1/wordpress/transcription/files?page=${page}&size=${size}&sort=${encodeURIComponent(sort)}`, cfg)
  const headers = await buildHeaders(cfg)

  return request<FileListResponse>(url, { headers })
}

/**
 * Lista transcrições do usuário usando o endpoint baseado em TranscriptionAnalysis.
 * Retorna informações completas de progresso e estado de processamento.
 */
export async function listTranscriptions(params?: { page?: number; size?: number }): Promise<TranscriptionListResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const { page = 0, size = 20 } = params || {}
  const url = buildAiUrl(`/api/v1/transcription/list?page=${page}&size=${size}`, cfg)
  const headers = await buildHeaders(cfg)

  return request<TranscriptionListResponse>(url, { headers })
}

/**
 * Converte TranscriptionListItem para FileItem para manter compatibilidade.
 */
function transcriptionToFileItem(t: TranscriptionListItem): FileItem {
  // Mapear processingState para status legado
  let status = 'processing'
  if (t.processingState === 'COMPLETED') {
    status = 'completed'
  } else if (t.processingState === 'FAILED') {
    status = 'failed'
  }
  
  return {
    fileId: t.fileId,
    title: t.title,
    type: t.extension,
    bytes: t.bytes ?? 0,
    status,
    currentState: t.processingState ?? null,
    currentSubState: t.processingSubState,
    progress: t.progressPercent,
    message: t.progressMessage,
    createdAt: t.createdAt?.toString() ?? null,
    completedAt: t.analysisCompletedAt?.toString() ?? null,
    transcriptionCompletedAt: t.transcriptionCompletedAt ?? null,
    analysisCompletedAt: t.analysisCompletedAt ?? null,
    // Novos campos
    processingState: t.processingState,
    estimatedSecondsRemaining: t.estimatedSecondsRemaining,
    processingStartedAt: t.processingStartedAt,
    estimatedCompletionAt: t.estimatedCompletionAt
  }
}

/**
 * Lista arquivos usando o novo endpoint baseado em TranscriptionAnalysis.
 * Retorna no formato FileListResponse para manter compatibilidade com o UI existente.
 */
export async function listFilesV2(params?: { page?: number; size?: number }): Promise<FileListResponse> {
  const data = await listTranscriptions(params)
  
  // Tratar caso onde a resposta não tem os campos esperados
  const files = data?.files || []
  
  return {
    files: files.map(transcriptionToFileItem),
    totalFiles: data?.totalFiles || 0,
    page: data?.page || 0,
    pageSize: data?.pageSize || 20,
    totalPages: data?.totalPages || 0
  }
}

export async function createUpload(file: File): Promise<UploadCreateResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const body = {
    title: file.name,
    bytes: file.size,
    type: getFileExtension(file.name, file.type)
  }

  const url = buildFileUrl('/api/v1/wordpress/transcription/create', cfg)
  const headers = await buildHeaders(cfg)
  return request<UploadCreateResponse>(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  })
}

export async function uploadToPresigned(presignedUrl: string, headers: Record<string, string>, file: File) {
  const mergedHeaders = new Headers(headers)
  if (!mergedHeaders.has('Content-Type')) {
    mergedHeaders.set('Content-Type', file.type || 'application/octet-stream')
  }
  const res = await fetch(presignedUrl, {
    method: 'PUT',
    headers: mergedHeaders,
    body: file
  })
  if (!res.ok) {
    throw new Error('Falha ao enviar para storage')
  }
}

export async function confirmUpload(fileId: string) {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildFileUrl(`/api/v1/wordpress/transcription/${fileId}/success`, cfg)
  const headers = await buildHeaders(cfg)
  return request(url, { method: 'PUT', headers })
}

/**
 * Atualiza o mapeamento de speakers (nomes personalizados)
 */
export async function updateSpeakerMapping(fileId: string, speakerMapping: Record<string, string>): Promise<{ status: string; message: string; speakerMapping: Record<string, string> }> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/speaker`, cfg)
  const headers = await buildHeaders(cfg)
  return request(url, { 
    method: 'PUT', 
    headers,
    body: JSON.stringify(speakerMapping)
  })
}

export async function failUpload(fileId: string) {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildFileUrl(`/api/v1/wordpress/transcription/${fileId}/fail`, cfg)
  const headers = await buildHeaders(cfg)
  return request(url, { method: 'PUT', headers })
}

export async function getStatus(fileId: string): Promise<StatusResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildFileUrl(`/api/v1/wordpress/transcription/${fileId}/status`, cfg)
  const headers = await buildHeaders(cfg)
  return request<StatusResponse>(url, { headers })
}

export async function deleteFile(fileId: string) {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildFileUrl(`/api/v1/wordpress/transcription/${fileId}/delete`, cfg)
  const headers = await buildHeaders(cfg)
  return request(url, { method: 'DELETE', headers })
}

export async function downloadFile(fileId: string): Promise<DownloadResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildFileUrl(`/api/v1/wordpress/transcription/${fileId}/download`, cfg)
  const headers = await buildHeaders(cfg)
  return request<DownloadResponse>(url, { headers })
}

// ===============================================
// ANÁLISE DE TRANSCRIÇÕES - NOVOS ENDPOINTS
// ===============================================

import type { 
  Transcription, 
  TranscriptionMetrics,
  SentimentAnalysis,
  EntityExtraction,
  TopicDetection,
  EventTimeline,
  SpeechPatternAnalysis,
  AISummary,
  MultiTranscriptionComparison,
  UserAnnotations
} from '../types/transcription'
import { isAnalysisNotAvailable } from '../types/transcription'

export type TranscriptionResponse = {
  transcription: Transcription
  metrics: TranscriptionMetrics
}

/**
 * Status de cada tipo de análise
 * Valores possíveis: PENDING, PROCESSING, COMPLETED, FAILED
 */
export type AnalysisStatus = {
  overall: string
  sentiment: string
  entities: string
  topics: string
  patterns: string
  summary: string
  timeline: string
  analysisTime: number
  errorMessage: string | null
}

/**
 * Obtém o status atual de cada análise para polling
 * Frontend deve chamar este endpoint periodicamente para atualizar a UI
 */
export async function getAnalysisStatus(fileId: string): Promise<AnalysisStatus> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/analysis-status`, cfg)
  const headers = await buildHeaders(cfg)
  return request<AnalysisStatus>(url, { headers })
}

/**
 * Obtém transcrição completa com métricas
 */
export async function getTranscription(fileId: string): Promise<TranscriptionResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}`, cfg)
  const headers = await buildHeaders(cfg)
  return request<TranscriptionResponse>(url, { headers })
}

/**
 * Obtém análise de sentimento
 * @returns null se a análise ainda não está disponível (arquivo em processamento)
 */
export async function getSentimentAnalysis(fileId: string): Promise<SentimentAnalysis | null> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/sentiment`, cfg)
  const headers = await buildHeaders(cfg)
  const result = await request<SentimentAnalysis>(url, { headers })
  if (isAnalysisNotAvailable(result)) return null
  return result
}

/**
 * Obtém extração de entidades
 * @returns null se a análise ainda não está disponível (arquivo em processamento)
 */
export async function getEntityExtraction(fileId: string): Promise<EntityExtraction | null> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/entities`, cfg)
  const headers = await buildHeaders(cfg)
  const result = await request<EntityExtraction>(url, { headers })
  if (isAnalysisNotAvailable(result)) return null
  return result
}

/**
 * Obtém detecção de tópicos
 * @returns null se a análise ainda não está disponível (arquivo em processamento)
 */
export async function getTopicDetection(fileId: string): Promise<TopicDetection | null> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/topics`, cfg)
  const headers = await buildHeaders(cfg)
  const result = await request<TopicDetection>(url, { headers })
  if (isAnalysisNotAvailable(result)) return null
  return result
}

/**
 * Obtém timeline de eventos
 * @returns null se a análise ainda não está disponível (arquivo em processamento)
 */
export async function getEventTimeline(fileId: string): Promise<EventTimeline | null> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/timeline`, cfg)
  const headers = await buildHeaders(cfg)
  const result = await request<EventTimeline>(url, { headers })
  if (isAnalysisNotAvailable(result)) return null
  return result
}

/**
 * Obtém análise de padrões de fala
 * @returns null se a análise ainda não está disponível (arquivo em processamento)
 */
export async function getSpeechPatternAnalysis(fileId: string): Promise<SpeechPatternAnalysis | null> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/patterns`, cfg)
  const headers = await buildHeaders(cfg)
  const result = await request<SpeechPatternAnalysis>(url, { headers })
  if (isAnalysisNotAvailable(result)) return null
  return result
}

/**
 * Obtém resumo gerado por IA
 * @returns null se a análise ainda não está disponível (arquivo em processamento)
 */
export async function getAISummary(fileId: string): Promise<AISummary | null> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/summary`, cfg)
  const headers = await buildHeaders(cfg)
  const result = await request<AISummary>(url, { headers })
  if (isAnalysisNotAvailable(result)) return null
  return result
}

/**
 * Obtém anotações do usuário para uma transcrição
 */
export async function getAnnotations(fileId: string): Promise<UserAnnotations> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/annotations`, cfg)
  const headers = await buildHeaders(cfg)
  try {
    const result = await request<UserAnnotations>(url, { headers })
    return result
  } catch {
    // Retorna estrutura vazia se não existir
    return { items: [], highlights: [], bookmarks: [] }
  }
}

/**
 * Atualiza anotações do usuário para uma transcrição
 */
export async function updateAnnotations(fileId: string, annotations: UserAnnotations): Promise<UserAnnotations> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/annotations`, cfg)
  const headers = await buildHeaders(cfg)
  return request<UserAnnotations>(url, {
    method: 'PUT',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(annotations)
  })
}

/**
 * Compara múltiplas transcrições
 */
export async function compareTranscriptions(fileIds: string[]): Promise<MultiTranscriptionComparison> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl('/api/v1/transcription/compare', cfg)
  const headers = await buildHeaders(cfg)
  return request<MultiTranscriptionComparison>(url, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileIds })
  })
}

/**
 * Tipos de arquivo suportados pelo backend (enum MediaFileType)
 */
const SUPPORTED_EXTENSIONS = ['MP3', 'WAV', 'M4A', 'OGG', 'MP4'] as const
type SupportedExtension = typeof SUPPORTED_EXTENSIONS[number]

/**
 * Extrai a extensão do arquivo e retorna em maiúsculo.
 * Se não for uma extensão suportada, tenta inferir pelo MIME type.
 */
function getFileExtension(fileName: string, mimeType?: string): SupportedExtension {
  // Extrai extensão do nome do arquivo
  const match = fileName.match(/\.([a-zA-Z0-9]+)$/)
  if (match) {
    const ext = match[1].toUpperCase()
    if (SUPPORTED_EXTENSIONS.includes(ext as SupportedExtension)) {
      return ext as SupportedExtension
    }
  }

  // Fallback: tenta inferir pelo MIME type
  if (mimeType) {
    const mimeToExt: Record<string, SupportedExtension> = {
      'audio/mpeg': 'MP3',
      'audio/mp3': 'MP3',
      'audio/wav': 'WAV',
      'audio/wave': 'WAV',
      'audio/x-wav': 'WAV',
      'audio/m4a': 'M4A',
      'audio/x-m4a': 'M4A',
      'audio/mp4': 'M4A',
      'audio/ogg': 'OGG',
      'video/mp4': 'MP4',
      'video/mpeg': 'MP4'
    }
    const extFromMime = mimeToExt[mimeType.toLowerCase()]
    if (extFromMime) return extFromMime
  }

  // Default para áudio genérico
  return 'MP3'
}
